import { DataTable } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { TWOS_COMPLEMENT_CALCULATOR_STRINGS } from "@/components/tools/twos-complement-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  groupNibbles,
  signedDecimal,
  superscript,
} from "@/lib/tool-kit/cs-tools/bits";
import type { Analysis, NegateSteps } from "@/lib/tool-kit/cs-tools/twos";
import type { TwosFormat } from "@/lib/tool-kit/cs-tools/twos-eval";

type Strings = typeof TWOS_COMPLEMENT_CALCULATOR_STRINGS;

function StepRows({
  steps,
  labels,
  strings,
  width,
}: {
  steps: NegateSteps;
  labels: readonly [string, string, string];
  strings: Strings;
  width: number;
}) {
  return (
    <DataTable
      caption={strings.stepsCaption}
      head={[strings.stepColumn, strings.rowColumn]}
      rows={[
        [t(labels[0], { width }), groupNibbles(steps.start)],
        [labels[1], groupNibbles(steps.inverted)],
        [labels[2], groupNibbles(steps.result)],
      ]}
    />
  );
}

function Working({
  a,
  format,
  strings,
}: {
  a: Analysis;
  format: TwosFormat;
  strings: Strings;
}) {
  const power = `2${superscript(a.width)}`;
  const stored = signedDecimal(a.signed);
  if (format === "decimal" && !a.fits) {
    return (
      <p className="text-muted-foreground text-sm">
        {t(strings.stepWrapped, { power, width: a.width, stored })}
      </p>
    );
  }
  if (!a.negative) {
    return (
      <p className="text-muted-foreground text-sm">
        {format === "decimal"
          ? strings.stepNonNegative
          : t(strings.stepNonNegativeRead, { value: a.signed.toString() })}
      </p>
    );
  }
  const magnitude = (-a.signed).toString();
  if (format === "decimal" && a.fromMagnitude) {
    return (
      <div className="space-y-2">
        <StepRows
          labels={[
            strings.stepMagnitude,
            strings.stepInvert,
            strings.stepAddOne,
          ]}
          steps={a.fromMagnitude}
          strings={strings}
          width={a.width}
        />
        <p className="text-muted-foreground text-sm">
          {t(strings.stepShortcut, {
            power,
            magnitude,
            pattern: a.unsigned.toString(),
            total: a.range.count.toString(),
          })}
        </p>
      </div>
    );
  }
  return a.negation ? (
    <div className="space-y-2">
      <StepRows
        labels={[
          strings.stepPatternFirst,
          strings.stepInvertBack,
          strings.stepAddOneBack,
        ]}
        steps={a.negation}
        strings={strings}
        width={a.width}
      />
      <p className="text-muted-foreground text-sm">
        {t(strings.stepSizeIs, { magnitude })}
      </p>
    </div>
  ) : null;
}

function termText(a: Analysis): string {
  return a.terms
    .map((term, index) => {
      const size = (
        term.weight < BigInt(0) ? -term.weight : term.weight
      ).toString();
      if (term.weight < BigInt(0)) {
        return `−${size}`;
      }
      return index === 0 ? size : `+ ${size}`;
    })
    .join(" ");
}

function FormsTable({ a, strings }: { a: Analysis; strings: Strings }) {
  const show = (bits: string | null) =>
    bits === null ? strings.formNone : groupNibbles(bits);
  return (
    <DataTable
      caption={strings.tableCaption}
      head={[strings.formName, strings.formBits]}
      rows={[
        [strings.formSignMagnitude, show(a.forms.signMagnitude)],
        [strings.formOnes, show(a.forms.onesComplement)],
        [strings.formTwos, show(a.forms.twosComplement)],
      ]}
    />
  );
}

export function TwosViews({
  analysis: a,
  format,
  strings,
}: {
  analysis: Analysis;
  format: TwosFormat;
  strings: Strings;
}) {
  const top = `2${superscript(a.width - 1)}`;
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <Working a={a} format={format} strings={strings} />
      </section>
      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.weightsHeading}</h3>
        <p className="text-muted-foreground text-sm">
          {t(strings.weightsBody, { top })}
        </p>
        <p className="break-words font-mono text-foreground text-sm">
          {a.terms.length === 0
            ? strings.weightsNone
            : t(strings.weightsSum, {
                terms: termText(a),
                value: signedDecimal(a.signed),
              })}
        </p>
      </section>
      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.formsHeading}</h3>
        <FormsTable a={a} strings={strings} />
      </section>
      {a.extended ? (
        <section className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.extendHeading}</h3>
          <p className="break-words text-muted-foreground text-sm">
            {t(strings.extendBody, { width: a.width * 2 })}{" "}
            <span className="font-mono text-foreground">
              {groupNibbles(a.extended)}
            </span>
          </p>
        </section>
      ) : null}
    </div>
  );
}
